// ForwardToCoordinatorModal.tsx
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { updateLegalResearchRequest } from "services/legalResearchService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";

interface ForwardToCoordinatorModalProps {
  data: any;
  setLegalResearch: Function;
  isOpen: boolean;
  onClose: () => void;
}

const ForwardToCoordinatorModal: React.FC<ForwardToCoordinatorModalProps> = ({
  data: singleData,
  setLegalResearch,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const contextData = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [remark, setRemark] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append("remark_for_coordinator", remark);
    data.append(
      "forward_status",
      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value,
    );

    try {
      const response = await updateLegalResearchRequest(
        singleData.id,
        data,
        contextData.token,
      );

      setLegalResearch((prev: any) => ({
        ...prev,
        ...response,
      }));

      showToast(t("LegalResearchForwardedSuccess"), "success");
      onClose();
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-xl font-semibold text-gray-800 mb-6">
          {t("ForwardToCoordinator")}
        </h2>

        {/* Remark Section */}
        <div className="mb-4">
          <label
            htmlFor="remark"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t("Remark")}
          </label>
          <textarea
            id="remark"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
            placeholder={t("DetailHere")}
            value={remark}
            name="remark"
            onChange={(e) => setRemark(e.target.value)}
          ></textarea>
        </div>

        {/* Forward Button */}
        <div className="text-center mt-6">
          <button
            type="submit"
            disabled={submitting}
            className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-8 rounded-md w-full disabled:opacity-50"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                {LoadingIcon} {t("Submitting")}
              </div>
            ) : (
              t("ForwardToCoordinator")
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ForwardToCoordinatorModal;
